import client from "./client";
import { isValidPaymentUrl } from "./subscription";

export type TrackPurchaseStatus = "queued" | "processing" | "redirect" | "succeeded" | "error";
export type TrackPurchasePaymentMode = "new" | "saved";
export type TrackPurchaseGroupBy = "none" | "artists" | "albums";

export interface TrackPurchaseJob {
  id: string;
  status: TrackPurchaseStatus;
  track_count: number;
  amount: string;
  currency: "RUB";
  group_by: TrackPurchaseGroupBy;
  payment_mode: TrackPurchasePaymentMode;
  attempts?: number;
  payment_url?: string | null;
  result_code?: string | null;
}

export interface TrackPurchaseResponse {
  success: boolean;
  message?: string;
  job?: TrackPurchaseJob;
  execution_enabled?: boolean;
}

export type TrackPurchaseResult =
  | { state: "redirect"; paymentUrl: string; job: TrackPurchaseJob }
  | { state: "succeeded"; job: TrackPurchaseJob }
  | { state: "pending"; job: TrackPurchaseJob; executionEnabled: boolean };

const DEFAULT_POLL_DELAYS_MS = [800, 1_500, 2_500, 4_000, 6_000] as const;

export async function submitTrackPurchase(
  trackIds: number[],
  groupBy: TrackPurchaseGroupBy,
  paymentMode: TrackPurchasePaymentMode,
  idempotencyKey: string,
  signal?: AbortSignal
): Promise<TrackPurchaseResponse> {
  const { data } = await client.post<TrackPurchaseResponse>(
    "/media/track-purchases/checkout.php",
    { track_ids: trackIds, group_by: groupBy, payment_mode: paymentMode },
    { signal, headers: { "Idempotency-Key": idempotencyKey } }
  );
  return data;
}

export async function getTrackPurchaseStatus(id: string, signal?: AbortSignal): Promise<TrackPurchaseResponse> {
  const { data } = await client.get<TrackPurchaseResponse>(
    `/media/track-purchases/status.php?id=${encodeURIComponent(id)}`,
    { signal }
  );
  return data;
}

export async function submitAndPollTrackPurchase(
  trackIds: number[],
  groupBy: TrackPurchaseGroupBy,
  paymentMode: TrackPurchasePaymentMode,
  options: {
    signal?: AbortSignal;
    idempotencyKey?: string;
    pollDelaysMs?: readonly number[];
    onStatus?: (job: TrackPurchaseJob, pollNumber: number) => void;
  } = {}
): Promise<TrackPurchaseResult> {
  const key = options.idempotencyKey ?? createIdempotencyKey();
  const delays = options.pollDelaysMs ?? DEFAULT_POLL_DELAYS_MS;
  let response = await submitTrackPurchase(trackIds, groupBy, paymentMode, key, options.signal);
  if (!response.success || !response.job) throw new Error(response.message || "Checkout was not created");
  let job = response.job;

  for (let poll = 0; ; poll += 1) {
    options.onStatus?.(job, poll);
    if (job.status === "redirect") {
      if (!job.payment_url || !isValidPaymentUrl(job.payment_url)) {
        throw new Error("Платёжная ссылка отклонена как небезопасная");
      }
      return { state: "redirect", paymentUrl: job.payment_url, job };
    }
    if (job.status === "succeeded") return { state: "succeeded", job };
    if (job.status === "error") throw new Error(response.message || "Не удалось создать платёж");
    if (poll >= delays.length) {
      return { state: "pending", job, executionEnabled: response.execution_enabled === true };
    }
    await abortableDelay(delays[poll], options.signal);
    response = await getTrackPurchaseStatus(job.id, options.signal);
    if (!response.success || !response.job) throw new Error(response.message || "Статус платежа недоступен");
    job = response.job;
  }
}

function createIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") return `track-purchase-${cryptoApi.randomUUID()}`;
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return `track-purchase-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(new DOMException("Checkout cancelled", "AbortError"));
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    const onAbort = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Checkout cancelled", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
